import type { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import type { TranslationProvider } from "../../utils/translate";
import styles from "./translatePage.module.scss";

const GOOGLE_TRANSLATE_URL = "https://translate.google.com/";
const MYMEMORY_URL = "https://mymemory.translated.net/";

// The "powered by Google Translate" badge, byte-for-byte the "color-regular"
// PNGs (1x/2x/3x) from Google's own download on
// https://docs.cloud.google.com/translate/attribution (fetched 2026-10-10).
// Google's rules: show it next to Google's results, link it to
// translate.google.com, and never alter it - so it keeps its native 176x16 size.
const GOOGLE_BADGE_DIR = "/images/google-translate-attribution";
const GOOGLE_BADGE_SRCSET = [
    `${GOOGLE_BADGE_DIR}/color-regular.png 1x`,
    `${GOOGLE_BADGE_DIR}/color-regular@2x.png 2x`,
    `${GOOGLE_BADGE_DIR}/color-regular@3x.png 3x`,
].join(", ");

// <Trans> puts the translated link text inside this anchor.
function MyMemoryLink({ children }: { children?: ReactNode }) {
    return (
        <a href={MYMEMORY_URL} target="_blank" rel="noopener noreferrer" className={styles.attributionLink}>
            {children}
        </a>
    );
}

// Caption under a translation. It names whichever service actually produced
// the text on screen (translateTextWithProvider reports it): the Google badge
// is only true for Google's results, and the learner may be looking at a
// MyMemory fallback - which also gets a plain-words warning, since it is often
// rougher than Google's and the small credit line alone does not say so.
export default function TranslationAttribution({ provider }: { provider: TranslationProvider }) {
    const { t } = useTranslation("translatePage");

    if (provider === "google") {
        return (
            <p className={styles.attribution} data-provider="google">
                <span>{t("attribution.automatic")}</span>
                <a href={GOOGLE_TRANSLATE_URL} target="_blank" rel="noopener noreferrer" className={styles.attributionBadgeLink}>
                    <img
                        src={`${GOOGLE_BADGE_DIR}/color-regular.png`}
                        srcSet={GOOGLE_BADGE_SRCSET}
                        width={176}
                        height={16}
                        lang="en"
                        alt={t("attribution.googleBadgeAlt")}
                    />
                </a>
            </p>
        );
    }

    return (
        <>
            <p className={styles.fallbackNotice} data-fallback="true">
                <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" />
                <span>{t("fallbackNotice")}</span>
            </p>
            <p className={styles.attribution} data-provider="mymemory">
                <Trans t={t} i18nKey="attribution.myMemory" components={{ mymemory: <MyMemoryLink /> }} />
            </p>
        </>
    );
}
